import React from 'react';
import { addTargetBlankToLinks } from '@/lib/linkUtils';
import './ConversationCardMetrics.css';

interface DailyMetric {
  Id: number;
  LayerId: number;
  LayerName?: string;
  LayerColor?: string;
  CurrentDate?: string;
  Status?: string;
  ActualHours?: number;
  ProducedHours?: number;
  ReworkHours?: number;
  ImpedimentHours?: number;
  EstimatedHoursRemaining?: number;
  ProgressPercentage?: number;
  HadRework?: boolean;
  ReworkType?: string;
  HadImpediment?: boolean;
  ImpedimentType?: string;
  RequirementChanges?: boolean;
  Severity?: string;
  FeedBackReviewed?: boolean;
  FeedBackReviewResult?: string;
  FeedBackReviewer?: string;
  CompletedPost?: boolean;
  UsedXREF?: boolean;
  ValidateADC?: boolean;
  Comment?: string;
}

interface LayerGroup {
  layerId: number;
  layerName: string;
  layerColor: string;
  metrics: DailyMetric[];
}

interface MetricSummary {
  totalActual: number;
  totalProduced: number;
  totalRework: number;
  totalImpediments: number;
  remaining: number;
  progress: number;
  status: string;
}

interface ConversationCardMetricsProps {
  conversation: { ConversationId: number };
  metrics: DailyMetric[];
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  WIP: { bg: '#ffe32b', color: '#000' },
  Done: { bg: '#98ff2b', color: '#000' },
  WFA: { bg: '#4ef9ff', color: '#000' },
  WFLFB: { bg: '#e44eff', color: '#000' },
  Cancelled: { bg: '#b41c1f', color: '#fff' },
  Hold: { bg: '#ef3e1b', color: '#fff' },
};

function formatDate(dateString?: string): string
{
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
}

function calculateSummary(metrics: DailyMetric[]): MetricSummary
{
  if (!metrics || metrics.length === 0)
  {
    return { totalActual: 0, totalProduced: 0, totalRework: 0, totalImpediments: 0, remaining: 0, progress: 0, status: 'N/A' };
  }

  let totalActual = 0;
  let totalProduced = 0;
  let totalRework = 0;
  let totalImpediments = 0;

  for (const m of metrics)
  {
    totalActual += m.ActualHours || 0;
    totalProduced += m.ProducedHours || 0;
    totalRework += m.ReworkHours || 0;
    totalImpediments += m.ImpedimentHours || 0;
  }

  const first = metrics[0]!;
  return {
    totalActual,
    totalProduced,
    totalRework,
    totalImpediments,
    remaining: first.EstimatedHoursRemaining || 0,
    progress: first.ProgressPercentage || 0,
    status: first.Status || 'N/A',
  };
}

function groupMetricsByLayer(metrics: DailyMetric[]): LayerGroup[]
{
  const groups: Record<number, LayerGroup> = {};

  for (const metric of metrics)
  {
    const id = metric.LayerId;
    if (!groups[id])
    {
      groups[id] = {
        layerId: id,
        layerName: metric.LayerName || 'Unknown Layer',
        layerColor: metric.LayerColor || '#e0e0e0',
        metrics: [],
      };
    }
    groups[id]!.metrics.push(metric);
  }

  return Object.values(groups);
}

class ConversationCardMetrics extends React.Component<ConversationCardMetricsProps>
{
  render()
  {
    const { conversation, metrics } = this.props;
    const layerGroups = groupMetricsByLayer(metrics || []);

    if (layerGroups.length === 0)
    {
      return (
        <div style={{ padding: '16px', color: '#666', fontFamily: 'Roboto, sans-serif', fontSize: '13px' }}>
          No metrics available for this card.
        </div>
      );
    }

    return (
      <div className="corgee_com_card-conversation-item">
        {layerGroups.map(layerGroup =>
        {
          const summary = calculateSummary(layerGroup.metrics);
          const summaryStatusStyle = STATUS_COLORS[summary.status] || { bg: '#e0e0e0', color: '#000' };

          return (
            <div key={'layer-' + layerGroup.layerId} style={{ marginBottom: '24px' }}>
              <div style={{
                padding: '8px 16px',
                borderRadius: '4px 4px 0 0',
                border: '1px solid ' + layerGroup.layerColor,
              }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: layerGroup.layerColor }}>
                  {layerGroup.layerName}
                </h3>
              </div>

              <div style={{
                border: '1px solid ' + layerGroup.layerColor,
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                padding: '16px',
                marginBottom: '16px',
                backgroundColor: '#fff',
                fontFamily: 'Roboto, sans-serif',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ margin: 0, color: '#4887be', fontSize: '16px', fontWeight: 600 }}>Summary</h4>
                  <span style={{
                    backgroundColor: summaryStatusStyle.bg,
                    color: summaryStatusStyle.color,
                    padding: '6px 16px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}>
                    {summary.status}
                  </span>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    <span>Progress {summary.progress}%</span>
                    <span>{summary.remaining}h remaining</span>
                  </div>
                  <div style={{ backgroundColor: '#e0e0e0', borderRadius: '4px', height: '10px', overflow: 'hidden' }}>
                    <div style={{
                      backgroundColor: '#62accb',
                      height: '100%',
                      width: summary.progress + '%',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                  {[
                    { label: 'Total Actual', value: summary.totalActual, bg: '#f0f9ff', color: '#62accb' },
                    { label: 'Total Produced', value: summary.totalProduced, bg: '#f0fff8', color: '#62cb8a' },
                    { label: 'Total Rework', value: summary.totalRework, bg: '#fefce8', color: '#f59e0b' },
                    { label: 'Total Impediments', value: summary.totalImpediments, bg: '#fef2f2', color: '#ef4444' },
                  ].map(item => (
                    <div key={item.label} style={{ textAlign: 'center', padding: '12px', backgroundColor: item.bg, borderRadius: '6px' }}>
                      <div style={{ fontSize: '18px', fontWeight: 600, color: item.color }}>{item.value}h</div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 style={{ margin: '0 0 12px 0', padding: '16px', color: '#4887be', fontSize: '16px', fontWeight: 600, fontFamily: 'Roboto, sans-serif' }}>
                  Historical
                </h4>

                {layerGroup.metrics.map(metric =>
                {
                  const statusStyle = STATUS_COLORS[metric.Status || ''] || { bg: '#e0e0e0', color: '#000' };

                  return (
                    <div
                      key={'metric-' + conversation.ConversationId + '-' + metric.Id}
                      className="corgee_com_card-conversation-item-metric-container"
                      style={{
                        border: '1px solid ' + (metric.LayerColor || '#e0e0e0'),
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '12px',
                        backgroundColor: '#fff',
                        fontFamily: 'Roboto, sans-serif',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '12px' }}>
                        <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                          {formatDate(metric.CurrentDate)}
                        </span>
                        <span style={{
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color,
                          padding: '4px 12px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}>
                          {metric.Status}
                        </span>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
                        {[
                          { label: 'Actual', value: metric.ActualHours, color: '#62accb' },
                          { label: 'Produced', value: metric.ProducedHours, color: '#22c55e' },
                          { label: 'Rework', value: metric.ReworkHours, color: '#f59e0b' },
                          { label: 'Impediment', value: metric.ImpedimentHours, color: '#ef4444' },
                        ].map(item => (
                          <div key={item.label} style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: item.color }}>{item.value}h</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>{item.label}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                          <span>Progress: {metric.ProgressPercentage}%</span>
                          <span>{metric.EstimatedHoursRemaining}h remaining</span>
                        </div>
                        <div style={{ backgroundColor: '#e0e0e0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                          <div style={{
                            backgroundColor: '#62accb',
                            height: '100%',
                            width: (metric.ProgressPercentage || 0) + '%',
                            transition: 'width 0.3s ease',
                          }} />
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                        {metric.HadRework && (
                          <span style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                            Rework: {metric.ReworkType}
                          </span>
                        )}
                        {metric.HadImpediment && (
                          <span style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                            Impediment: {metric.ImpedimentType}
                          </span>
                        )}
                        {metric.RequirementChanges && (
                          <span style={{ backgroundColor: '#ede9fe', color: '#5b21b6', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                            Req. Changes: {metric.Severity}
                          </span>
                        )}
                        {metric.FeedBackReviewed && (
                          <span style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                            Review: {metric.FeedBackReviewResult} by {metric.FeedBackReviewer}
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '16px', fontSize: '12px', marginBottom: '12px' }}>
                        <span style={{ color: metric.CompletedPost ? '#22c55e' : '#9ca3af' }}>
                          {metric.CompletedPost ? '✓' : '✗'} POST
                        </span>
                        <span style={{ color: metric.UsedXREF ? '#22c55e' : '#9ca3af' }}>
                          {metric.UsedXREF ? '✓' : '✗'} XREF
                        </span>
                        <span style={{ color: metric.ValidateADC ? '#22c55e' : '#9ca3af' }}>
                          {metric.ValidateADC ? '✓' : '✗'} ADC
                        </span>
                      </div>

                      {metric.Comment && (
                        <>
                          <span style={{ fontWeight: 'bold', fontSize: '12px' }}>Note: </span>
                          <div style={{ backgroundColor: '#f9fafb', padding: '10px', borderRadius: '4px', fontSize: '12px', color: '#374151' }}>
                            <div style={{ wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: addTargetBlankToLinks(metric.Comment) }} />
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}

export default ConversationCardMetrics;
